import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../../../../contexts/AuthContext';
import SearchSelect from '../../../../components/SearchSelect';

interface AssignableProposal {
  id: string;
  reference_code?: string;
  property_name?: string;
  sender_user_id?: string | null;
  assigned_manager_id?: string | null;
  is_system_lead?: boolean;
  proposal_data?: { sender_preferences?: { use_pos_email?: boolean } } | null;
}

interface ActiveUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string | null;
  pos_sender_email: string | null;
  permissions: Record<string, unknown> | null;
}

// Tier from the user's permissions._scope OR top-level role.
// Belt + braces: if permissions is sparse for any reason, role
// catches super admins so the picker still narrows correctly.
function userScope(u: ActiveUser): string {
  const s = (u.permissions as Record<string, unknown> | null | undefined)?.['_scope'];
  return typeof s === 'string' ? s : '';
}
function isAdminTier(u: ActiveUser): boolean {
  if (u.role === 'super_admin') return true;
  const s = userScope(u);
  return s === 'global' || s === 'all';
}
function isManagerOrAbove(u: ActiveUser): boolean {
  if (u.role === 'super_admin') return true;
  const s = userScope(u);
  return s === 'global' || s === 'all' || s === 'territory';
}

interface ProposalAssignModalProps {
  proposal: AssignableProposal;
  supabase: SupabaseClient;
  onClose: () => void;
  onSaved?: () => void;
}

export default function ProposalAssignModal({ proposal, supabase, onClose, onSaved }: ProposalAssignModalProps) {
  const { session } = useAuth();
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [senderId, setSenderId] = useState<string>(proposal.sender_user_id || '');
  const [managerId, setManagerId] = useState<string>(proposal.assigned_manager_id || '');
  const [sendAsPos, setSendAsPos] = useState<boolean>(
    proposal.proposal_data?.sender_preferences?.use_pos_email === true,
  );
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const { data, error: usersErr } = await supabase
        .from('users')
        .select('id, first_name, last_name, email, role, pos_sender_email, permissions')
        .eq('status', 'active')
        .order('first_name', { ascending: true });
      if (usersErr) {
        setError(usersErr.message);
      } else {
        setUsers((data || []) as ActiveUser[]);
      }
      setLoading(false);
    })();
  }, [supabase]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const SUPABASE_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_SUPABASE_URL;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/pos-proposal-manage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          action: 'assign',
          id: proposal.id,
          sender_user_id: senderId || null,
          assigned_manager_id: managerId || null,
          send_as_pos: sendAsPos,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `Save failed (${res.status})`);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const userLabel = (u: ActiveUser) =>
    `${[u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email} (${u.email})`;

  // Sender picker: admins only for system leads, managers+ otherwise.
  // Manager picker: managers and above, always. Operators / ambassadors
  // never appear in either dropdown to keep the list scannable.
  const senderOptions = proposal.is_system_lead
    ? users.filter(isAdminTier)
    : users.filter(isManagerOrAbove);
  const managerOptions = users.filter(isManagerOrAbove);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Assign Sender + Manager</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p className="text-sm text-muted mb-lg">
            Proposal: <strong>{proposal.reference_code}</strong>
            {proposal.property_name ? ` , ${proposal.property_name}` : ''}
          </p>
          {error && <div className="alert alert-error mb-lg">{error}</div>}

          <div className="form-group">
            <label className="form-label">Sender</label>
            <div className="text-xs text-muted mb-xs">
              The human signing this proposal. Drives the contact buttons on the customer view. Required before Send.
            </div>
            <SearchSelect
              disabled={loading}
              options={[{ value: '', label: '— not set —' }, ...senderOptions.map(u => ({ value: u.id, label: userLabel(u) }))]}
              value={senderId}
              onChange={setSenderId}
            />
            {proposal.is_system_lead && (
              <div className="text-xs text-muted mt-xs">
                System lead, showing admins only.
              </div>
            )}
          </div>

          {(() => {
            const selectedSender = users.find(u => u.id === senderId);
            if (!selectedSender?.pos_sender_email) return null;
            return (
              <div className="form-group" style={{ marginTop: '0.5rem', marginLeft: '0.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-base)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={sendAsPos}
                    onChange={(e) => setSendAsPos(e.target.checked)}
                  />
                  <span>
                    Send as Peace of Sleep (<code style={{ fontSize: 'var(--text-md)' }}>{selectedSender.pos_sender_email}</code>)
                  </span>
                </label>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                  Customer sees the peaceofsleep.vip address instead of cleanmybed.com on the proposal page and outbound email.
                </div>
              </div>
            );
          })()}

          <div className="form-group mt-lg">
            <label className="form-label">Assigned Manager</label>
            <div className="text-xs text-muted mb-xs">
              Operational owner of this lead. Setting this also unhides ad-lead proposals for the manager.
            </div>
            <SearchSelect
              disabled={loading}
              options={[{ value: '', label: '— not set —' }, ...managerOptions.map(u => ({ value: u.id, label: userLabel(u) }))]}
              value={managerId}
              onChange={setManagerId}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
